<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Violations"
      subtitle="Manage student violation records"
      icon="fas fa-exclamation-triangle"
      gradient="blue"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="6"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableViolations.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Status"
          label-for="filterStatus"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableViolations.selected.status"
            placeholder="filter status"
            input-id="filterStatus"
            label="name"
            clearable
            append-to-body
            :options="statusOptions"
            :disabled="tableViolations.busy"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ name }">
              <div
                class="text-break text-wrap"
                v-text="name"
              />
            </template>
            <template #no-options="">
              no available status
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Violation Type"
          label-for="filterViolationType"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableViolations.selected.violationType"
            placeholder="filter type"
            input-id="filterViolationType"
            label="name"
            clearable
            append-to-body
            :options="tableViolations.options.violationTypes"
            :loading="tableViolations.fetching.violationTypes"
            :disabled="tableViolations.busy || tableViolations.fetching.violationTypes"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ name }">
              <div
                class="text-break text-wrap"
                v-text="name"
              />
            </template>
            <template #no-options="">
              no available type
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol cols="12">
        <BButton
          size="sm"
          type="button"
          variant="primary"
          @click.prevent="createViolation"
        >
          Record Violation
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableViolations"
      :provider="tableProvider"
      :fields="tableViolations.fields"
      :filter="tableViolations.filter"
      :filter-on="tableViolations.filterOn"
      :sort-by="tableViolations.sortBy"
      :sort-desc="tableViolations.sortDesc"
      :sort-direction="tableViolations.sortDirection"
      :total-rows="tableViolations.totalRows"
      :current-page="tableViolations.currentPage"
      :per-page="tableViolations.perPage"
      :stacked="tableViolations.stacked"
      :busy="tableViolations.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableViolations.busy"
            @click.prevent="editViolation(item)"
          >
            Edit
          </BButton>
        </div>
      </template>

      <template #cell(student)="{ item }">
        {{ item.student ? `${item.student.first_name} ${item.student.last_name}` : 'N/A' }}
      </template>

      <template #cell(violation_type)="{ item }">
        {{ item.violation_type ? item.violation_type.name : 'N/A' }}
      </template>

      <template #cell(status)="{ item }">
        <BBadge :variant="getStatusVariant(item.status)">
          {{ formatStatus(item.status) }}
        </BBadge>
      </template>

      <template #cell(evidence)="{ item }">
        <BButton
          v-if="item.evidence_url"
          size="sm"
          variant="outline-primary"
          :disabled="tableViolations.busy"
          @click.prevent="openEvidencePreview(item)"
        >
          View
        </BButton>
        <span
          v-else
          class="text-muted"
        >N/A</span>
      </template>
    </CustomTable>

    <BModal
      ref="modalViolation"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="state.editing ? 'Edit Violation' : 'Record Violation'"
    >
      <ValidationObserver ref="formViolation">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="student_id"
                name="student"
                rules="required"
              >
                <BFormGroup
                  label="Student"
                  label-for="fStudent"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.student"
                    placeholder="select student"
                    input-id="fStudent"
                    label="full_name"
                    append-to-body
                    :options="options.students"
                    :loading="fetching.students"
                    :disabled="state.busy || fetching.students"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  >
                    <template #option="{ full_name, id_number }">
                      <div class="text-break text-wrap">
                        {{ full_name }} ({{ id_number }})
                      </div>
                    </template>
                    <template #selected-option="{ full_name }">
                      <div
                        class="text-truncate"
                        v-text="full_name"
                      />
                    </template>
                    <template #no-options="">
                      no available students
                    </template>
                  </VSelect>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="violation_type_id"
                name="violation type"
                rules="required"
              >
                <BFormGroup
                  label="Violation Type"
                  label-for="fViolationType"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.violationType"
                    placeholder="select violation type"
                    input-id="fViolationType"
                    label="name"
                    append-to-body
                    :options="options.violationTypes"
                    :loading="fetching.violationTypes"
                    :disabled="state.busy || fetching.violationTypes || state.editing"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  >
                    <template #option="{ name }">
                      <div
                        class="text-break text-wrap"
                        v-text="name"
                      />
                    </template>
                    <template #no-options="">
                      no available types
                    </template>
                  </VSelect>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="location"
                name="location"
                rules="max:255"
              >
                <BFormGroup
                  label="Location"
                  label-for="fLocation"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fLocation"
                    v-model="formViolation.location"
                    placeholder="where did this occur"
                    maxlength="255"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="occurred_at"
                name="occurred at"
                rules="required"
              >
                <BFormGroup
                  label="Occurred At"
                  label-for="fOccurredAt"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fOccurredAt"
                    v-model="formViolation.occurred_at"
                    type="datetime-local"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="status"
                name="status"
                rules="required"
              >
                <BFormGroup
                  label="Status"
                  label-for="fStatus"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.status"
                    placeholder="select status"
                    input-id="fStatus"
                    label="name"
                    append-to-body
                    :options="statusOptions"
                    :disabled="state.busy"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  >
                    <template #option="{ name }">
                      <div
                        class="text-break text-wrap"
                        v-text="name"
                      />
                    </template>
                  </VSelect>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <BFormGroup
                label="Evidence Image"
                label-for="fEvidenceFile"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormFile
                  id="fEvidenceFile"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  :disabled="state.busy"
                  placeholder="Choose evidence image..."
                  drop-placeholder="Drop image here..."
                  @change="onEvidenceFileChange"
                />
                <small class="text-muted">Optional. Max file size: 5MB.</small>
              </BFormGroup>
            </BCol>

            <BCol
              v-if="formViolation.evidence_url"
              cols="12"
            >
              <BFormGroup
                label="Current Evidence"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BButton
                  size="sm"
                  variant="outline-primary"
                  :disabled="state.busy"
                  @click.prevent="openEvidencePreview(formViolation)"
                >
                  View Current Evidence
                </BButton>
              </BFormGroup>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="description"
                name="description"
                rules="max:1000"
              >
                <BFormGroup
                  label="Description"
                  label-for="fDescription"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormTextarea
                    id="fDescription"
                    v-model="formViolation.description"
                    placeholder="describe the violation"
                    rows="3"
                    maxlength="1000"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateViolation"
        >
          {{ state.editing ? 'Update' : 'Create' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>

    <BModal
      ref="modalEvidencePreview"
      title="Violation Evidence"
      size="lg"
      centered
      hide-footer
    >
      <div
        v-if="state.loadingEvidence"
        class="text-center py-4"
      >
        <i class="fas fa-spinner fa-spin fa-2x text-primary" />
      </div>

      <div
        v-else-if="evidencePreviewUrl"
        class="text-center"
      >
        <img
          :src="evidencePreviewUrl"
          alt="Violation evidence"
          class="img-fluid rounded border"
        >
      </div>

      <div
        v-else
        class="text-center text-muted py-4"
      >
        No evidence image available.
      </div>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import QUERY from 'lodash'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminViolation, SSharedList } from '@/services'

export default {
  name: 'VAdminViolations',

  components: {
    AdminPageHeader
  },

  mixins: [
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Violations'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false,
        loadingEvidence: false
      },
      evidenceFile: null,
      evidencePreviewUrl: '',
      selected: {
        student: '',
        violationType: '',
        status: ''
      },
      options: {
        students: [],
        violationTypes: []
      },
      fetching: {
        students: false,
        violationTypes: false
      },
      statusOptions: [
        { id: 'pending', name: 'Pending' },
        { id: 'resolved', name: 'Resolved' },
        { id: 'dismissed', name: 'Dismissed' }
      ],
      formViolation: {
        id: '',
        student_id: '',
        violation_type_id: '',
        user_id: '',
        description: '',
        location: '',
        occurred_at: '',
        status: 'pending',
        evidence_url: ''
      },
      tableViolations: {
        busy: false,
        fetching: {
          violationTypes: false
        },
        options: {
          violationTypes: []
        },
        selected: {
          status: null,
          violationType: null
        },
        filter: {
          search: '',
          status: '',
          violation_type_id: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-80px' },
          { mobile: 1, key: 'student', label: 'Student', class: 'min-w-150px' },
          { mobile: 2, key: 'violation_type', label: 'Type', class: 'min-w-120px' },
          { mobile: 3, key: 'evidence', class: 'text-center min-w-90px' },
          { mobile: 4, key: 'location', class: 'min-w-120px' },
          { mobile: 5, key: 'occurred_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 6, key: 'status', class: 'text-center min-w-100px' },
          { mobile: 7, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {
    'selected.student' (student) {
      this.formViolation.student_id = student?.id || ''
    },
    'selected.violationType' (type) {
      this.formViolation.violation_type_id = type?.id || ''
    },
    'selected.status' (status) {
      this.formViolation.status = status?.id || ''
    },
    'tableViolations.selected.status' (status) {
      this.tableViolations.filter.status = status?.id || ''
    },
    'tableViolations.selected.violationType' (type) {
      this.tableViolations.filter.violation_type_id = type?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getViolationTypes(this.tableViolations)
    }
  },

  beforeDestroy () {
    if (this.evidencePreviewUrl) {
      URL.revokeObjectURL(this.evidencePreviewUrl)
      this.evidencePreviewUrl = ''
    }
  },

  methods: {
    getStatusVariant (status) {
      const variants = {
        pending: 'warning',
        resolved: 'success',
        dismissed: 'secondary'
      }
      return variants[status] || 'secondary'
    },

    formatStatus (status) {
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A'
    },

    formatStudent (student) {
      if (!student) return 'N/A'
      const lastName = student.last_name || ''
      const firstName = student.first_name || ''
      const combined = `${lastName}${lastName && firstName ? ', ' : ''}${firstName}`.trim()
      return combined || 'N/A'
    },

    async getStudents () {
      this.fetching.students = true
      return SSharedList.getActiveStudents({ per_page: 1000, sort: 'last_name', sort_desc: false })
        .then(({ data }) => {
          this.options.students = (data.data || []).map(s => ({
            ...s,
            full_name: `${s.first_name} ${s.last_name}`
          }))
        })
        .catch(() => { this.options.students = [] })
        .finally(() => { this.fetching.students = false })
    },

    async getViolationTypes (target) {
      target.fetching.violationTypes = true
      return SSharedList.getActiveViolationTypes({ per_page: 500, sort: 'name', sort_desc: false })
        .then(({ data }) => {
          target.options.violationTypes = data.data || []
        })
        .catch(() => { target.options.violationTypes = [] })
        .finally(() => { target.fetching.violationTypes = false })
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableViolations.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        status: filter.status,
        violation_type_id: filter.violation_type_id
      }

      return await SAdminViolation.getList(payload).then(
        ({ data }) => {
          this.tableViolations.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableViolations.totalRows = 0
        return []
      }).finally(() => {
        this.tableViolations.busy = false
      })
    },

    createViolation () {
      this.state.editing = false

      this.formViolation = Object.assign(this.formViolation, {
        id: '',
        student_id: '',
        violation_type_id: '',
        user_id: this.currentUser.id,
        description: '',
        location: '',
        occurred_at: '',
        status: 'pending',
        evidence_url: ''
      })

      this.selected.student = ''
      this.selected.violationType = ''
      this.selected.status = QUERY.find(this.statusOptions, { id: 'pending' }) || ''
      this.evidenceFile = null

      this.getStudents()
      this.getViolationTypes(this)

      this.$refs.modalViolation.show()
    },

    editViolation (violation) {
      this.state.editing = true

      const occurredAt = violation.occurred_at
        ? violation.occurred_at.replace(' ', 'T').substring(0, 16)
        : ''

      this.formViolation = Object.assign(this.formViolation, {
        id: violation.id,
        student_id: violation.student_id,
        violation_type_id: violation.violation_type_id,
        user_id: violation.user_id || this.currentUser.id,
        description: violation.description,
        location: violation.location,
        occurred_at: occurredAt,
        status: violation.status,
        evidence_url: violation.evidence_url || ''
      })
      this.evidenceFile = null

      this.getStudents().finally(() => {
        this.selected.student = QUERY.find(this.options.students, {
          id: Number(violation.student_id)
        }) || ''
      })

      this.getViolationTypes(this).finally(() => {
        this.selected.violationType = QUERY.find(this.options.violationTypes, {
          id: Number(violation.violation_type_id)
        }) || ''
      })

      this.selected.status = QUERY.find(this.statusOptions, {
        id: violation.status
      }) || ''

      this.$refs.modalViolation.show()
    },

    async validateViolation () {
      await this.$refs.formViolation.validate().then(
        async validForm => {
          if (!validForm) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Violation?</h6>' : '<h6>Record this Violation?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const payload = {
                ...this.formViolation,
                occurred_at: this.formViolation.occurred_at
                  ? this.formViolation.occurred_at.replace('T', ' ') + ':00'
                  : ''
              }

              const formData = new FormData()
              Object.entries(payload).forEach(([key, value]) => {
                if (value !== null && value !== undefined && key !== 'evidence_url') {
                  formData.append(key, value)
                }
              })

              if (this.evidenceFile) {
                formData.append('evidence_file', this.evidenceFile)
              }

              const createOrUpdate = async () => {
                return this.state.editing
                  ? SAdminViolation.update(this.formViolation.id, formData)
                  : SAdminViolation.store(formData)
              }

              return createOrUpdate().then(
                ({ data: { message } }) => {
                  this.swalSuccess(message).then(() => {
                    this.$refs.modalViolation.hide()
                    this.$refs.tableViolations.refetch()
                  })
                }
              ).catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    this.$refs.formViolation.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.evidenceFile = null
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    getEvidenceFilename (evidenceUrl) {
      if (!evidenceUrl || typeof evidenceUrl !== 'string') return ''
      const parts = evidenceUrl.split('/')
      return parts.length ? parts[parts.length - 1] : ''
    },

    onEvidenceFileChange (event) {
      const file = event?.target?.files?.[0] || null
      this.evidenceFile = file
    },

    async openEvidencePreview (violation) {
      const evidenceUrl = violation?.evidence_url
      const filename = this.getEvidenceFilename(evidenceUrl)
      if (!filename) {
        if (this.evidencePreviewUrl) {
          URL.revokeObjectURL(this.evidencePreviewUrl)
        }
        this.evidencePreviewUrl = ''
        this.$refs.modalEvidencePreview.show()
        return
      }

      this.state.loadingEvidence = true
      if (this.evidencePreviewUrl) {
        URL.revokeObjectURL(this.evidencePreviewUrl)
      }
      this.evidencePreviewUrl = ''
      this.$refs.modalEvidencePreview.show()

      try {
        const { data } = await SAdminViolation.getEvidence(filename)
        this.evidencePreviewUrl = URL.createObjectURL(data)
      } catch {
        this.evidencePreviewUrl = ''
      } finally {
        this.state.loadingEvidence = false
      }
    }
  }
}
</script>
